import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronRight, Layers, Link2, Music2, Plus, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Constructeur de programme (maquette utilisateur) : nom + mode Concert/Répétition.
// Mode Répétition : blocs par axes de travail du groupe. Mode Concert : liste unique ordonnée.
// Morceaux piochés dans la bibliothèque via une feuille de recherche (filtres = catégories).

const fmtDur = (s) => (s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : "");
const pieceMeta = (p) => [p?.composer, fmtDur(p?.duration_sec)].filter(Boolean).join(" · ");

// Index globaux des éléments d'un bloc (ordre conservé dans `program`)
const idxsOf = (program, mode, axis) =>
  program.map((p, i) => ((mode === "concert" ? true : p.axis === axis) ? i : -1)).filter((i) => i >= 0);

// NB : ItemRow et BlockSection sont volontairement au niveau MODULE — définis dans le composant,
// chaque frappe recréait leur type, React remontait le champ et le clavier perdait le focus.
const ItemRow = ({ p, i, k, total, axis, program, nodeMap, t, onMove, onRemove, onNote }) => (
  <div className="border border-app rounded-xl px-3 py-2.5" data-testid={`program-item-${i}`}>
    <div className="flex items-center gap-2.5">
      <Music2 className="h-4 w-4 accent shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold uppercase tracking-wide truncate">{nodeMap[p.node_id]?.name}</p>
        {pieceMeta(nodeMap[p.node_id]) && <p className="text-xs t-secondary truncate">{pieceMeta(nodeMap[p.node_id])}</p>}
      </div>
      <button type="button" onClick={() => onMove(axis, k, -1)} disabled={k === 0} className="t-secondary hover:text-white disabled:opacity-30" data-testid={`program-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
      <button type="button" onClick={() => onMove(axis, k, 1)} disabled={k === total - 1} className="t-secondary hover:text-white disabled:opacity-30" data-testid={`program-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
      <button type="button" onClick={() => onRemove(i)} className="t-secondary hover:text-rose-400" data-testid={`program-remove-${i}`}><X className="h-4 w-4" /></button>
    </div>
    <Input
      value={p.note}
      onChange={(e) => onNote(i, e.target.value)}
      placeholder={t("agd.note_optional")}
      className="mt-2 h-9 text-base md:text-xs bg-transparent border-app"
      data-testid={`program-note-${i}`}
    />
  </div>
);

const BlockSection = ({ axis, program, mode, nodeMap, t, renaming, renameVal, setRenaming, setRenameVal, onRenameBlock, onPick, onMove, onRemove, onNote }) => {
  const idxs = idxsOf(program, mode, axis);
  return (
    <div data-testid={`program-block-${axis || "concert"}`}>
      <div className="flex items-center gap-2">
        <span className="w-1.5 h-6 rounded-full accent-bg shrink-0" aria-hidden="true" />
        {renaming === axis ? (
          <Input
            autoFocus
            value={renameVal}
            onChange={(e) => setRenameVal(e.target.value)}
            onBlur={() => onRenameBlock(axis)}
            onKeyDown={(e) => { if (e.key === "Enter") onRenameBlock(axis); if (e.key === "Escape") setRenaming(null); }}
            className="h-8 max-w-[220px] bg-transparent border-app font-bold"
            data-testid="program-block-rename-input"
          />
        ) : (
          <h3 className="font-heading text-lg font-bold" data-testid={`program-block-title-${axis || "concert"}`}>
            {axis || t("dash.program")} <span className="text-sm font-normal t-secondary">({idxs.length})</span>
          </h3>
        )}
        <span className="ml-auto flex items-center gap-4">
          {mode === "repetition" && axis && (
            <button
              type="button"
              onClick={() => { setRenaming(axis); setRenameVal(axis); }}
              className="text-xs t-secondary hover:text-white flex items-center gap-1.5 transition-colors"
              data-testid={`program-block-rename-${axis.replace(/\s/g, "-")}`}
            >
              <Layers className="h-4 w-4" /> {t("agd.block")}
            </button>
          )}
          <button
            type="button"
            onClick={() => onPick(axis)}
            className="text-sm accent font-semibold flex items-center gap-1 hover:underline"
            data-testid={`program-pick-${axis.replace(/\s/g, "-") || "concert"}`}
          >
            <Plus className="h-4 w-4" /> {t("agd.pick")}
          </button>
        </span>
      </div>
      {idxs.length === 0 ? (
        <p className="text-sm t-secondary mt-2 ml-4" data-testid={`program-block-empty-${axis || "concert"}`}>{t("agd.block_empty")}</p>
      ) : (
        <div className="mt-2.5 ml-4 space-y-2">
          {idxs.map((i, k) => (
            <ItemRow key={i} p={program[i]} i={i} k={k} total={idxs.length} axis={axis} program={program} nodeMap={nodeMap} t={t} onMove={onMove} onRemove={onRemove} onNote={onNote} />
          ))}
        </div>
      )}
    </div>
  );
};

export const ProgramBuilder = ({ program, setProgram, pieces, nodeMap, groupAxes, name, setName, mode, setMode }) => {
  const { t } = useAuth();
  const [pickFor, setPickFor] = useState(null); // axe cible ("" = liste concert)
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [extraBlocks, setExtraBlocks] = useState([]);
  const [newBlock, setNewBlock] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState("");

  // Blocs visibles : axes du groupe + blocs ajoutés + axes déjà présents dans le programme
  const axes = useMemo(() => {
    const inProg = [...new Set(program.map((p) => p.axis).filter(Boolean))];
    return [...new Set([...groupAxes, ...extraBlocks, ...inProg])];
  }, [groupAxes, extraBlocks, program]);

  const categories = useMemo(() => {
    const seen = new Map();
    pieces.forEach((p) => {
      const c = nodeMap[p.parent_id];
      if (c && !seen.has(c.id)) seen.set(c.id, c.name);
    });
    return [...seen.entries()].map(([id, nm]) => ({ id, name: nm }));
  }, [pieces, nodeMap]);

  const moveIn = (axis, k, dir) => {
    const idxs = idxsOf(program, mode, axis);
    const a = idxs[k];
    const b = idxs[k + dir];
    if (a === undefined || b === undefined) return;
    const arr = [...program];
    [arr[a], arr[b]] = [arr[b], arr[a]];
    setProgram(arr);
  };

  const removeItem = (i) => setProgram(program.filter((_, xi) => xi !== i));

  const setNote = (i, v) => setProgram(program.map((x, xi) => (xi === i ? { ...x, note: v } : x)));

  const addItem = (pieceId) => {
    setProgram([...program, { axis: pickFor || "", node_id: pieceId, note: "" }]);
    setPickFor(null);
    setSearch("");
    setCatFilter("all");
    toast.success(t("agd.piece_added"));
  };

  const renameBlock = (axis) => {
    const v = renameVal.trim();
    if (v && v !== axis) {
      setProgram(program.map((p) => (p.axis === axis ? { ...p, axis: v } : p)));
      setExtraBlocks(extraBlocks.map((a) => (a === axis ? v : a)));
    }
    setRenaming(null);
  };

  const addBlock = () => {
    const v = newBlock.trim();
    if (v && !axes.includes(v)) setExtraBlocks([...extraBlocks, v]);
    setNewBlock("");
  };

  const filteredPieces = pieces.filter((p) => {
    if (catFilter !== "all" && p.parent_id !== catFilter) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const hay = [p.name, p.composer, ...(p.tags || [])].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });

  return (
    <div className="space-y-5" data-testid="program-builder">
      <p className="text-sm accent flex items-center gap-2" data-testid="program-linked-label">
        <Link2 className="h-4 w-4" /> {t("agd.program_linked")}
      </p>

      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t("agd.program_name_ph")}
        className="h-12 text-base bg-white/5 border-app rounded-xl"
        data-testid="program-name-input"
      />

      <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-white/5" data-testid="program-mode-toggle">
        {[["concert", "agd.mode_concert"], ["repetition", "agd.mode_repetition"]].map(([m, key]) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`py-2.5 rounded-lg text-sm font-semibold transition-colors ${mode === m ? "accent-bg text-slate-900" : "t-secondary hover:text-white"}`}
            data-testid={`program-mode-${m}`}
          >
            {t(key)}
          </button>
        ))}
      </div>

      {mode === "concert" ? (
        <BlockSection axis="" program={program} mode={mode} nodeMap={nodeMap} t={t} renaming={renaming} renameVal={renameVal} setRenaming={setRenaming} setRenameVal={setRenameVal} onRenameBlock={renameBlock} onPick={setPickFor} onMove={moveIn} onRemove={removeItem} onNote={setNote} />
      ) : (
        <div className="space-y-6">
          {axes.map((a) => (
            <BlockSection key={a} axis={a} program={program} mode={mode} nodeMap={nodeMap} t={t} renaming={renaming} renameVal={renameVal} setRenaming={setRenaming} setRenameVal={setRenameVal} onRenameBlock={renameBlock} onPick={setPickFor} onMove={moveIn} onRemove={removeItem} onNote={setNote} />
          ))}
          <div className="flex gap-2">
            <Input
              value={newBlock}
              onChange={(e) => setNewBlock(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addBlock())}
              placeholder={t("agd.block_rename_ph")}
              className="bg-transparent border-app"
              data-testid="program-new-block-input"
            />
            <button
              type="button"
              onClick={addBlock}
              disabled={!newBlock.trim()}
              className="shrink-0 text-sm accent font-semibold flex items-center gap-1 px-3 rounded-lg border border-dashed border-app hover:bg-white/5 disabled:opacity-40"
              data-testid="program-add-block"
            >
              <Plus className="h-4 w-4" /> {t("agd.add_block")}
            </button>
          </div>
        </div>
      )}

      {/* Feuille « Choisir un morceau » */}
      <Dialog open={pickFor !== null} onOpenChange={(v) => !v && setPickFor(null)}>
        <DialogContent className="card-bg border-app max-w-lg max-h-[85vh] flex flex-col" data-testid="program-picker">
          <DialogHeader><DialogTitle className="font-heading">{t("agd.choose_piece")}</DialogTitle></DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 t-secondary" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("agd.piece_search_ph")}
              className="pl-9 bg-white/5 border-app"
              data-testid="program-picker-search"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="program-picker-cats">
            <button
              type="button"
              onClick={() => setCatFilter("all")}
              className={`shrink-0 text-xs font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${catFilter === "all" ? "accent-bg text-slate-900 border-transparent" : "border-app t-secondary hover:bg-white/5"}`}
              data-testid="program-picker-cat-all"
            >
              {t("agd.all_styles")}
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCatFilter(c.id)}
                className={`shrink-0 text-xs font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${catFilter === c.id ? "accent-bg text-slate-900 border-transparent" : "border-app t-secondary hover:bg-white/5"}`}
                data-testid={`program-picker-cat-${c.id}`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto space-y-2" data-testid="program-piece-list">
            {filteredPieces.length === 0 && <p className="text-sm t-secondary py-8 text-center">{t("agd.no_piece")}</p>}
            {filteredPieces.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => addItem(p.id)}
                className="w-full text-left flex items-center gap-3 border border-app rounded-xl px-3.5 py-3 hover:bg-white/5 transition-colors"
                data-testid={`program-piece-option-${p.id}`}
              >
                <Music2 className="h-4 w-4 accent shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-bold uppercase tracking-wide truncate">{p.name}</span>
                  <span className="block text-xs t-secondary truncate">{[p.composer || "—", nodeMap[p.parent_id]?.name].filter(Boolean).join(" · ")}</span>
                </span>
                <ChevronRight className="h-4 w-4 t-secondary shrink-0" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
